import { Modal, ModalProps } from "antd";
import { FC, PropsWithChildren, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Draggable, { type ControlPosition, type DraggableData, type DraggableEvent } from "react-draggable";
import { modalBody, modalContent, resizeDiv } from "./styles";

export interface ResizeableModalProps {
  modalProps: ModalProps;
  size: {
    height: number;
    minHeight?: number;
    minWidth?: number;
    width: number;
  };
}

const EDGE_PADDING = 8;
export const ResizeableModal: FC<PropsWithChildren<ResizeableModalProps>> = ({ children, modalProps, size }) => {
  const [dragDisabled, setDragDisabled] = useState(true);
  const [dragBounds, setDragBounds] = useState({ left: 0, top: 0, bottom: 0, right: 0 });
  const [maxSize, setMaxSize] = useState({ maxHeight: size.height, maxWidth: size.width });
  const [modalOpen, setModalOpen] = useState(false);

  const draggableRef = useRef<HTMLDivElement>(null);
  const resizeTimeoutId = useRef<number>();
  const resizeObserver = useMemo(
    () => new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const target = entry.target as HTMLElement;
        const fixOutOfBoundsSizes = () => {
          if (parseInt(target.style.height) > maxSize.maxHeight) {
            target.style.height = `${maxSize.maxHeight}px`;
          }
          if (parseInt(target.style.width) > maxSize.maxWidth) {
            target.style.width = `${maxSize.maxWidth}px`;
          }
        };

        window.clearTimeout(resizeTimeoutId.current);
        // this needs to be delayed a bit, to account for later changes of the element's size
        resizeTimeoutId.current = window.setTimeout(fixOutOfBoundsSizes, 100);
      });
    }),
    [maxSize.maxHeight, maxSize.maxWidth],
  );

  useEffect(
    () => {
      const draggableEl = draggableRef.current;
      if (modalOpen && draggableEl != null) {
        resizeObserver.observe(draggableEl);
        return () => resizeObserver.unobserve(draggableEl);
      }

      return undefined;
    },
    [modalOpen, resizeObserver],
  );

  const updateMaxSize = useCallback(
    (targetRect: DOMRect) => {
      const { innerHeight, innerWidth } = window;
      setMaxSize({
        maxHeight: innerHeight - targetRect.top - EDGE_PADDING,
        maxWidth: innerWidth - targetRect.left - EDGE_PADDING,
      });
    },
    [],
  );

  useEffect(
    () => {
      const resizeListener = () => {
        const targetRect = draggableRef.current?.getBoundingClientRect();
        if (modalOpen && targetRect != null) {
          updateMaxSize(targetRect);
        }
      };

      window.addEventListener("resize", resizeListener);
      return () => window.removeEventListener("resize", resizeListener);
    },
    [modalOpen, resizeObserver, updateMaxSize],
  );

  const { afterOpenChange: propsAfterOpenChange } = modalProps;
  const afterOpenChange = useCallback(
    (open: boolean) => {
      const targetRect = draggableRef.current?.getBoundingClientRect();
      if (open && targetRect != null) {
        updateMaxSize(targetRect);
      }

      propsAfterOpenChange?.(open);
      setModalOpen(open);
    },
    [propsAfterOpenChange, updateMaxSize],
  );

  const onDragStart = useCallback(
    (_: DraggableEvent, uiData: DraggableData) => {
      const targetRect = draggableRef.current?.getBoundingClientRect();
      if (modalOpen && targetRect != null) {
        const { innerHeight, innerWidth } = window;
        setDragBounds({
          left: -targetRect.left + uiData.x + EDGE_PADDING,
          right: innerWidth - (targetRect.right - uiData.x) - EDGE_PADDING,
          top: -targetRect.top + uiData.y + EDGE_PADDING,
          bottom: innerHeight - (targetRect.bottom - uiData.y) - EDGE_PADDING,
        });
      }
    },
    [modalOpen],
  );

  const onDragStop = useCallback(
    (_: DraggableEvent, __: DraggableData) => {
      const targetRect = draggableRef.current?.getBoundingClientRect();
      if (modalOpen && targetRect != null) {
        updateMaxSize(targetRect);
      }
    },
    [modalOpen, updateMaxSize],
  );

  const { modalRender: propsModalRender } = modalProps;
  const modalRender = useCallback(
    (modal: ReactNode) => {
      const { innerHeight, innerWidth } = window;
      const defaultPosition: ControlPosition = {
        x: innerWidth / 2 - size.width / 2,
        y: innerHeight / 2 - size.height / 2,
      };

      return (
        <Draggable bounds={dragBounds} defaultPosition={defaultPosition} disabled={dragDisabled} onStart={onDragStart} onStop={onDragStop}>
          <div ref={draggableRef} style={{ ...resizeDiv, ...size, ...maxSize }}>
            {propsModalRender?.(modal) ?? modal}
          </div>
        </Draggable>
      );
    },
    [dragBounds, dragDisabled, maxSize, onDragStart, onDragStop, propsModalRender, size],
  );

  const { styles: propsStyles } = modalProps;
  const styleOverrides = useMemo(
    () => ({
      ...(propsStyles ?? {}),
      body: {
        ...(propsStyles?.body ?? {}),
        ...modalBody,
      },
      content: {
        ...(propsStyles?.content ?? {}),
        ...modalContent,
      },
    }),
    [propsStyles],
  );

  const { title: propsTitle } = modalProps;
  const modalTitle = useMemo(
    () => (
      <div
        style={{ width: "100%", cursor: "move" }}
        onMouseOut={() => setDragDisabled(true)}
        onMouseOver={() => {
          if (dragDisabled) {
            setDragDisabled(false);
          }
        }}
      >
        {propsTitle}
      </div>
    ),
    [dragDisabled, propsTitle],
  );

  return (
    <Modal
      {...modalProps}
      afterOpenChange={afterOpenChange}
      centered={true}
      destroyOnHidden={true}
      height="100%"
      modalRender={modalRender}
      styles={styleOverrides}
      title={modalTitle}
      width="100%"
    >
      {children}
    </Modal>
  );
};
